import React, { useCallback, useEffect, useState } from "react";
import { apiUrl } from "../config/api";

type UsaProduct = {
  code: string;
  name: string;
  retailPrice: number;
  usaPrice: number | null;
  usaStock: number;
  imageUrl: string | null;
};

type Alert = { show: boolean; message: string; type?: "success" | "warning" | "info" | "danger" };

export default function USAStockPage(): JSX.Element {
  const [items, setItems] = useState<UsaProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [toast, setToast] = useState<Alert>({ show: false, message: "", type: "success" });

  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState<{ code: string; val: string } | null>(null);
  // PMN-CRITICAL(/USAStockPage): withPriceOnly toggle — TRUE = show only products with usa_price (เดิม). FALSE = also show products with USA stock > 0 even if usa_price IS NULL.
  const [withPriceOnly, setWithPriceOnly] = useState(true);

  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2200);
    return () => clearTimeout(t);
  }, [toast.show]);

  // PMN-CRITICAL(/USAStockPage): default list includes only products with usa_price. Toggle off withPriceOnly to also see products with USA stock that haven't been priced yet (e.g. just transferred via /ScanToUSAPage).
  const fetchData = useCallback(async (searchQ: string, pg: number, priceOnly: boolean) => {
    try {
      setLoading(true);
      setErrorMsg("");
      const token = localStorage.getItem("bg_shop_token") ?? "";
      const params = new URLSearchParams({
        q: searchQ,
        page: String(pg),
        limit: String(limit),
        withPriceOnly: priceOnly ? "true" : "false",
      });
      const res = await fetch(apiUrl(`/products-usa-stock?${params}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? res.statusText);
      if (data.ok) {
        setItems(data.products);
        setTotal(Number(data.total ?? 0));
        setPage(Number(data.page ?? pg));
      }
    } catch (err: any) {
      const msg = err?.message ?? "โหลดข้อมูลไม่สำเร็จ";
      setErrorMsg(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(q, 1, withPriceOnly);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, withPriceOnly]);

  const doSearch = () => {
    const qVal = qInput.trim();
    setQ(qVal);
    setPage(1);
    fetchData(qVal, 1, withPriceOnly);
  };

  const refresh = () => fetchData(q, page, withPriceOnly);

  // PMN-CRITICAL(/USAStockPage): Saving null usa_price removes the product from this page after refresh because the backend list filters usa_price IS NOT NULL.
  const handleSaveUsaPrice = async (code: string) => {
    if (!editingPrice || editingPrice.code !== code) return;
    try {
      const token = localStorage.getItem("bg_shop_token") ?? "";
      const price = editingPrice.val === "" ? null : Number(editingPrice.val);
      const res = await fetch(apiUrl(`/products/${code}/usa-price`), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ usa_price: price }),
      });
      if (!res.ok) throw new Error("failed");
      setEditingPrice(null);
      setToast({ show: true, message: "บันทึกราคา USA สำเร็จ", type: "success" });
      fetchData(q, page, withPriceOnly);
    } catch {
      setToast({ show: true, message: "บันทึกไม่สำเร็จ", type: "danger" });
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}>
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-9">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">

                {/* Header */}
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                  <h4 className="mb-0 text-primary">รายการสินค้า Stock USA</h4>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={refresh}>
                      รีเฟรช
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="input-group mb-2">
                  <input
                    className="form-control"
                    placeholder="ค้นหาโค้ด / ชื่อสินค้า (กด Enter เพื่อค้นหา)"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  />
                  <button className="btn btn-primary" type="button" onClick={doSearch}>
                    ค้นหา
                  </button>
                </div>

                {/* PMN-NOTE(/USAStockPage): toggle filter "เฉพาะที่ตั้งราคาแล้ว" — เปิด = พฤติกรรมเดิม (เฉพาะ usa_price IS NOT NULL); ปิด = รวมสินค้าที่มี USA stock แต่ยังไม่ตั้งราคา */}
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="withPriceOnlyToggle"
                    checked={withPriceOnly}
                    onChange={(e) => setWithPriceOnly(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="withPriceOnlyToggle">
                    เฉพาะสินค้าที่ตั้งราคา USA แล้ว{" "}
                    <span className="text-muted">
                      {withPriceOnly
                        ? "(ปิดเพื่อแสดงของที่มี stock ใน USA แต่ยังไม่ตั้งราคา)"
                        : "(แสดงทั้งที่ตั้งราคาแล้ว + ของใน USA stock ที่ยังไม่ตั้งราคา)"}
                    </span>
                  </label>
                </div>

                {/* Loading */}
                {loading && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังโหลดข้อมูล…</span>
                  </div>
                )}

                {/* Error */}
                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    โหลดข้อมูลไม่สำเร็จ: {errorMsg}
                  </div>
                )}

                {/* Table */}
                <div className="border rounded-3 p-0 bg-white">
                  <div className="table-responsive" style={{ maxHeight: 520 }}>
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: 76 }}>รูป</th>
                          <th style={{ width: 160 }}>โค้ด</th>
                          <th>ชื่อสินค้า</th>
                          <th style={{ width: 140 }} className="text-end">ราคาปลีก</th>
                          <th style={{ width: 180 }} className="text-end">ราคา USA</th>
                          <th style={{ width: 120 }} className="text-end">คงเหลือ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length === 0 && !loading && (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-4">ไม่พบรายการ</td>
                          </tr>
                        )}
                        {items.map((p) => (
                          <tr key={p.code} className="user-select-none">
                            <td>
                              {p.imageUrl ? (
                                <img
                                  src={p.imageUrl}
                                  alt={p.name}
                                  width={56}
                                  height={56}
                                  loading="lazy"
                                  decoding="async"
                                  style={{ objectFit: "cover", borderRadius: 8, background: "#f5f5f5" }}
                                  onMouseEnter={() => setPreviewImage(p.imageUrl)}
                                  onMouseLeave={() => setPreviewImage(null)}
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                              )}
                            </td>
                            <td className="fw-semibold">{p.code}</td>
                            <td>{p.name}</td>
                            <td className="text-end">
                              {p.retailPrice.toLocaleString("th-TH", { style: "currency", currency: "THB" })}
                            </td>
                            <td className="text-end">
                              {editingPrice?.code === p.code ? (
                                <div className="d-flex gap-1 justify-content-end">
                                  <input
                                    className="form-control form-control-sm text-end"
                                    style={{ width: 90 }}
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    value={editingPrice.val}
                                    onChange={(e) => setEditingPrice({ code: p.code, val: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") handleSaveUsaPrice(p.code);
                                      if (e.key === "Escape") setEditingPrice(null);
                                    }}
                                    autoFocus
                                  />
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => handleSaveUsaPrice(p.code)}
                                  >
                                    บันทึก
                                  </button>
                                  <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setEditingPrice(null)}
                                  >
                                    ยกเลิก
                                  </button>
                                </div>
                              ) : (
                                <span
                                  className="text-primary"
                                  style={{ cursor: "pointer" }}
                                  title="คลิกเพื่อแก้ไขราคา USA"
                                  onClick={() =>
                                    setEditingPrice({
                                      code: p.code,
                                      val: p.usaPrice !== null ? String(p.usaPrice) : "",
                                    })
                                  }
                                >
                                  {p.usaPrice !== null ? (
                                    p.usaPrice.toLocaleString("en-US", { style: "currency", currency: "USD" })
                                  ) : (
                                    <em className="text-muted">-</em>
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="text-end fw-semibold">{p.usaStock}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="small text-muted">
                    แสดง {items.length} รายการ / ทั้งหมด {total} รายการ
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page <= 1 || loading}
                      onClick={() => {
                        const p = page - 1;
                        setPage(p);
                        fetchData(q, p, withPriceOnly);
                      }}
                    >
                      ← ก่อนหน้า
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page * limit >= total || loading}
                      onClick={() => {
                        const p = page + 1;
                        setPage(p);
                        fetchData(q, p, withPriceOnly);
                      }}
                    >
                      ถัดไป →
                    </button>
                  </div>
                </div>

                <a href="/" className="btn btn-link text-decoration-none flex-fill text-center">
                  ← กลับหน้าเมนู
                </a>

              </div>
            </div>

            {/* Hover Preview กลางจอ */}
            {previewImage && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 2000, pointerEvents: "none" }}
              >
                <img
                  src={previewImage}
                  alt="preview"
                  style={{
                    maxWidth: "90%",
                    maxHeight: "90%",
                    borderRadius: 12,
                    boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                    background: "#fff",
                  }}
                />
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
